<template>
    <div class="muven-container">
        <div class="muven-router-name-container has-tour">
            <p class="muven-router-name">Pedidos</p>
            <FeatureTourCmp :tour="tour" :ready="!loading" />
        </div>

        <div class="muven-content">
            <Loading page v-if="loading" />

            <ConfirmationExclusionModalCmp 
                ref="confirmationExclusionModal"
                @confirm="confirmExclusionHandler" />

            <AuditListQuickVisualizationCmp 
                ref="auditListQuickVisualizationModal" />

            <div class="mb-3 order-toolbar" v-if="!loading">
                <div class="order-toolbar-actions">
                    <button type="button"
                        class="btn btn-primary"
                        @click="showFilterModal()">
                        Filtro
                    </button>

                    <button type="button"
                        class="btn btn-outline-secondary ml-3"
                        @click="openOrderImportSpecificModal()">
                        Importar pedido
                    </button>

                    <button type="button"
                        class="btn btn-outline-secondary ml-3"
                        v-if="subscriber.isEnviromentHML()"
                        :disabled="loadingFakeOrderModalInfo"
                        @click="showFakeOrderModal()">
                        Criar pedido de teste

                        <Loading v-show="loadingFakeOrderModalInfo" />
                    </button>
                </div>

                <div class="order-view-mode" data-tour="order-view-mode" role="group" aria-label="Formato de exibição da lista">
                    <span class="order-view-mode-caption">Exibir como</span>

                    <button type="button"
                        class="order-view-mode-option"
                        :class="{'is-active': isTimeline}"
                        :aria-pressed="isTimeline ? 'true' : 'false'"
                        @click="changeViewMode(VIEW_MODE_TIMELINE)">Linha do tempo</button>

                    <button type="button"
                        class="order-view-mode-option"
                        :class="{'is-active': !isTimeline}"
                        :aria-pressed="isTimeline ? 'false' : 'true'"
                        @click="changeViewMode(VIEW_MODE_FLAT)">Lista</button>
                </div>
            </div>

            <FilterInformationCmp 
                v-if="!loading"
                :filter-submitted="orderFilterSubmitted"
                :filter-properties="orderFilterProperties" />

            <OrderFlowCmp 
                ref="orderFlow" 
                @status-changed="statusChangedHandler" />

            <div class="modal" tabindex="-1" role="dialog" id="muven-order-import-specific-modal">
                <div class="modal-dialog modal-lg" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">
                                <span>Importação de pedido</span>
                            </h5>
                        </div>
                        <div class="modal-body">
                            <div>
                                <div class="row">
                                    <div class="col-12">
                                        <div class="muven-tip tip-info mb-3">
                                            <fa icon="info" class="tip-icon" />
                                            <p><b>ATENÇÃO!</b></p>
                                            <p>Esta funcionalidade apenas deve ser utilizada nos casos em que o Muven não recebeu o pedido do canal.</p>
                                            <p>Não utilize este formulário para atualizar as informações de um pedido já existente.</p>
                                        </div>
                                    </div>

                                    <div class="col-12 form-group">
                                        <label>Canal</label>
                                        <multiselect
                                            v-model="orderImportSpecific.channel"
                                            :options="channelListOptions"
                                            :multiple="false"
                                            :show-labels="false"
                                            label="name"
                                            track-by="enumChannel"
                                            placeholder="">
                                            <template slot="option" slot-scope="{ option }">
                                                <ChannelLogoCmp :channel="option" :size="18" />
                                            </template>
                                            <template slot="singleLabel" slot-scope="{ option }">
                                                <ChannelLogoCmp :channel="option" :size="18" />
                                            </template>
                                        </multiselect>
                                    </div>

                                    <div class="col-12 form-group">
                                        <label>Código do pedido</label>
                                        <input type="text" 
                                            class="form-control"
                                            v-model="orderImportSpecific.idExternalChannel">
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-primary" 
                                @click="importSpecificOrder(orderImportSpecific)"
                                :disabled="importingOrderSpecific || !orderImportSpecific.isValid()">
                                <fa icon="sync-alt" class="mr-1" spin v-show="importingOrderSpecific"/>

                                Importar
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeOrderImportSpecificModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal" tabindex="-1" role="dialog" id="muven-order-reimport-confirmation-modal">
                <div class="modal-dialog modal-lg" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">
                                <span>Reimportação de pedido</span>
                            </h5>
                        </div>
                        <div class="modal-body">
                            <div>
                                <p>Ao continuar, o pedido <b>{{chosenOrderReimport.idExternalChannel}}</b> do canal <b>{{chosenOrderReimport.channel.name}}</b> será reimportado.</p>
                                <p>Porém, para que não haja duplicação, o pedido anterior será cancelado tanto no Muven quanto na retaguarda. Mas não se preocupe, pois o status do canal não será alterado.</p>
                                <p class="mt-3">Tem certeza que deseja reimportar o pedido <b>{{chosenOrderReimport.idExternalChannel}}</b>?</p>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-outline-danger" 
                                @click="reimportOrder(chosenOrderReimport)"
                                :disabled="reimportingOrder">
                                <fa icon="exclamation-triangle" class="mr-1" v-show="!reimportingOrder"/>
                                <fa icon="sync-alt" class="mr-1" spin v-show="reimportingOrder"/>

                                Sim, reimportar
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeOrderReimportConfirmationModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal" tabindex="-1" role="dialog" id="muven-order-list-filter-modal">
                <div class="modal-dialog modal-lg" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Filtro de pedidos</h5>
                        </div>
                        <div class="modal-body">
                            <div class="row">
                                <div class="col-12 form-group">
                                    <label>Tipo de filtro</label>
                                    <div class="btn-group">
                                       <button v-for="(filterOption, indexFilterOption) in filterOptions" :key="indexFilterOption"
                                        type="button" 
                                        class="btn btn-outline-secondary"
                                        :class="{active: filterOption.active}"
                                        @click="updateFilterOption(filterOption)">{{filterOption.description}}</button>
                                    </div>
                                </div>
                            </div>

                            <div class="row" v-if="isFilterOptionByTrackingCodeInfo">
                                <div class="col-12 form-group">
                                    <label>Código de rastreio</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderFilter.trackingCode" />
                                </div>
                            </div>

                            <div class="row" v-if="isFilterOptionByChannelInfo">
                                <div class="col-12 form-group">
                                    <label>Id no canal</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderFilter.idExternalChannel" />
                                </div>
                            </div>

                            <div class="row" v-if="isFilterOptionByBackendInfo">
                                <div class="col-12 form-group">
                                    <label>Código na retaguarda</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderFilter.idSource" />
                                </div>
                            </div>

                            <div class="row" v-if="isFilterOptionAdvanced">
                                <div class="col-12 col-md-6 form-group">
                                    <label>Data inicial de criação no Muven</label>
                                    <v-date-picker 
                                        :input-props="{'class': 'form-control'}" 
                                        popover-visibility="focus"
                                        v-model="orderFilter.dateCreatedAtMuven.start" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Data final de criação no Muven</label>
                                    <v-date-picker 
                                        :input-props="{'class': 'form-control'}" 
                                        popover-visibility="focus"
                                        v-model="orderFilter.dateCreatedAtMuven.end" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Data inicial de venda no canal</label>
                                    <v-date-picker 
                                        :input-props="{'class': 'form-control'}" 
                                        popover-visibility="focus"
                                        v-model="orderFilter.date.start" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Data final de venda no canal</label>
                                    <v-date-picker 
                                        :input-props="{'class': 'form-control'}" 
                                        popover-visibility="focus"
                                        v-model="orderFilter.date.end" />
                                </div>

                                <div class="col-12 form-group" v-if="hasAccessToMoreThanOneChannel">
                                    <label>Canal</label>
                                    <multiselect
                                        v-model="orderFilter.channelList"
                                        :options="channelListOptions"
                                        :multiple="true"
                                        :show-labels="false"
                                        label="name"
                                        track-by="enumChannel"
                                        placeholder="">
                                        <template slot="option" slot-scope="{ option }">
                                            <ChannelLogoCmp :channel="option" :size="18" />
                                        </template>
                                        <template slot="tag" slot-scope="{ option, remove }">
                                            <span class="multiselect__tag channel-filter-tag">
                                                <ChannelLogoCmp :channel="option" :size="14" :show-name="false" />
                                                <span>{{option.name}}</span>
                                                <i aria-hidden="true" tabindex="1" class="multiselect__tag-icon"
                                                    @keypress.enter.prevent="remove(option)"
                                                    @mousedown.prevent="remove(option)"></i>
                                            </span>
                                        </template>
                                    </multiselect>
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Nome do cliente</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderFilter.customer" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Chave de acesso NFe</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderFilter.accessKey" />
                                </div>

                                <div class="col-12 form-group">
                                    <label>Status do pedido</label>
                                    <SelectWrapperCmp 
                                        :show-remove-button="!!orderFilter.statusList.length"
                                        @removed="handleOrderStatusListRemoval">
                                        <template v-slot:select>
                                            <multiselect
                                                v-model="orderFilter.statusList"
                                                :options="statusListOptions"
                                                :multiple="true"
                                                :show-labels="false"
                                                label="description"
                                                track-by="enum"
                                                placeholder="" />
                                        </template>
                                    </SelectWrapperCmp>
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Status da importação na retaguarda</label>
                                    <multiselect
                                        v-model="orderFilter.statusIdSource"
                                        :options="statusIdSourceListOptions"
                                        :multiple="false"
                                        :show-labels="false"
                                        label="description"
                                        track-by="enum"
                                        placeholder="" />
                                </div>

                                <div class="col-12 col-md-6 form-group">
                                    <label>Status do código de rastreio</label>
                                    <multiselect
                                        v-model="orderFilter.statusTrackingCode"
                                        :options="statusTrackingCodeListOptions"
                                        :multiple="false"
                                        :show-labels="false"
                                        label="description"
                                        track-by="enum"
                                        placeholder="" />
                                </div>

                                <div class="form-group col-12" v-show="hasMoreThanOneStore">
                                    <label>Loja</label>
                                    <SelectWrapperCmp 
                                        :show-remove-button="!!orderFilter.storeList.length"
                                        @removed="handleOrderStoreListRemoval">
                                        <template v-slot:select>
                                            <multiselect
                                                v-model="orderFilter.storeList"
                                                :options="storeList"
                                                :multiple="true"
                                                :show-labels="false"
                                                placeholder=""
                                                label="name"
                                                track-by="id" />
                                        </template>
                                    </SelectWrapperCmp>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-primary" 
                                @click="getOrdersWithFilter(orderFilter)"
                                :disabled="loadingFilter">
                                    Filtrar
                                    <Loading v-show="loadingFilter" />
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="clearFilter()">Restaurar</button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeFilterModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <OrderFakeCmp 
                ref="orderFakeModal" 
                :store-list="storeList"
                :channel-list-options="channelListOptions"
                @submitted="handleOrderFakeSubmission" />

            <div class="modal" tabindex="-1" role="dialog" id="muven-order-tracking-code-modal">
                <div class="modal-dialog" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Atualizar código de rastreio</h5>
                            <p class="modal-subtitle">Código: {{orderSelectedTrackingCode.idExternalChannel}}</p>
                        </div>
                        <div class="modal-body">
                            <div>
                                <div class="form-group">
                                    <label>Código de rastreio</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="orderTrackingCode.trackingCode" />
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-primary" 
                                @click="updateOrderTrackingCode(orderSelectedTrackingCode, orderTrackingCode)"
                                :disabled="!orderTrackingCode.isValid() || loadingUpdatingOrderTrackingCode">
                                    Gravar
                                    <Loading v-show="loadingUpdatingOrderTrackingCode" />
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeTrackingCodeModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <NothingFound v-if="nothingToShow" />

            <div v-if="!loading && !nothingToShow">
                <div :class="{'order-timeline': isTimeline, 'has-open-day': !!openDayGroup}">
                    <div class="table-responsive">
                        <table class="table table-sm table-hover">
                            <thead>
                                <tr>
                                    <th>Pedido</th>
                                    <th>Cliente</th>
                                    <th v-show="hasMoreThanOneStore">Loja</th>
                                    <th :class="isTimeline ? 'time-col' : 'datetime-col'">{{isTimeline ? 'Hora' : 'Momento venda'}}</th>
                                    <th class="value-col" title="Produtos + acréscimos - descontos. Não inclui frete.">Valor</th>
                                    <th class="status-col">Importação ret.</th>
                                    <th>Status</th>
                                    <th></th>
                                </tr>
                            </thead>

                            <tbody v-for="group in displayGroups" :key="group.key">
                                <tr v-if="!group.isHeaderless"
                                    class="timeline-day"
                                    data-tour="order-day"
                                    :class="{'is-today': group.isToday, 'is-open-day': group.isOpen}">
                                    <td :colspan="columnsBeforeValue">
                                        <span class="timeline-day-label">{{group.label}}</span>
                                        <InfoPopoverCmp v-if="group.isOpen"
                                            class="ledger-pop"
                                            title="Dia incompleto"
                                            :label="`${groupCountLabel(group)}, dia incompleto`"
                                            :width="240">
                                            <template #trigger>
                                                <span class="timeline-day-count is-open">{{groupCountLabel(group)}}</span>
                                            </template>
                                            <p>Podem existir mais pedidos deste dia nas próximas páginas.</p>
                                        </InfoPopoverCmp>
                                        <span v-else class="timeline-day-count">{{groupCountLabel(group)}}</span>
                                    </td>
                                    <!-- Subtotal do dia na própria coluna Valor: toda soma da tela mora na mesma vertical -->
                                    <td class="value-col timeline-day-value" :class="{'is-open': group.isOpen}">
                                        <InfoPopoverCmp class="ledger-pop"
                                            :title="group.isOpen ? 'Soma parcial do dia' : 'Soma do dia'"
                                            :label="`Soma do dia: ${currencyText(groupGross[group.key])}${group.isOpen ? ', parcial' : ''}`"
                                            :width="240">
                                            <template #trigger>
                                                <span>{{groupGross[group.key] | currency}}<span v-if="group.isOpen" class="ledger-plus">+</span></span>
                                            </template>
                                            <p>Pedidos do dia sem frete e sem cancelados.</p>
                                            <p v-if="group.isOpen">O dia pode continuar nas próximas páginas.</p>
                                        </InfoPopoverCmp>
                                    </td>
                                    <td :colspan="columnsAfterValue"></td>
                                </tr>

                                <tr v-for="order in group.orders"
                                    :key="order.id"
                                    class="order-row"
                                    :class="{'is-open-day': group.isOpen}"
                                    @click="openOrder(order, $event)">
                                    <!-- Identidade do pedido: canal e id formam uma coisa só ("o do ML final 2161") -->
                                    <td class="order-ident-cell">
                                        <div class="order-ident" data-tour="order-ident">
                                            <ChannelLogoCmp class="order-ident-logo"
                                                :channel="order.channel"
                                                :size="28"
                                                :label="order.idExternalChannel ? String(order.idExternalChannel) : '-'"
                                                :caption="order.channel.name || ' '" />
                                            <CopyButtonCmp v-if="order.idExternalChannel"
                                                class="order-id-copy"
                                                :value="order.idExternalChannel"
                                                label="Id do pedido no canal" />
                                        </div>
                                    </td>
                                    <td>
                                        <!-- Link de verdade: Ctrl/clique do meio abre em nova aba e há alvo para o teclado -->
                                        <router-link :to="detailUrl(order)" class="order-customer-link">{{customerName(order) | infoOrPlaceholder}}</router-link>
                                    </td>
                                    <td v-show="hasMoreThanOneStore">{{order.store.idSource | infoOrPlaceholder}}</td>
                                    <td :title="orderDateTitle(order)">{{orderDateLabel(order) | infoOrPlaceholder}}</td>
                                    <td class="value-col"
                                        :class="{'is-canceled': order.isCanceled()}"
                                        :title="orderValueTitle(order)">
                                        <fa v-if="order.hasDiscount()" icon="tags" class="order-value-discount" />
                                        <span class="order-value">{{order.getListValue() | currency}}</span>
                                    </td>
                                    <!-- Calmo no normal, alto na exceção: só o que pede atenção ganha cor -->
                                    <td class="order-import" :class="importTone(order)">
                                        <!-- Cancelado antes de ir para a retaguarda: o Muven não envia, então não está aguardando -->
                                        <span v-if="order.wasCanceledBeforeBackend()"
                                            title="O pedido foi cancelado antes de ir para a retaguarda. Pedidos cancelados não são enviados.">
                                            <fa icon="ban" fixed-width class="order-tone-icon" />
                                            Não enviado
                                        </span>
                                        <span v-else-if="order.hasIdSource()">
                                            <fa icon="check" fixed-width class="order-tone-icon" />
                                            Importado
                                        </span>
                                        <span v-else title="Ainda não importado na retaguarda">
                                            <fa icon="hourglass-start" fixed-width class="order-tone-icon" />
                                            Aguardando
                                        </span>
                                    </td>
                                    <td class="order-status" data-tour="order-status" :class="`is-${statusStyleOf(order).tone}`">
                                        <InfoPopoverCmp v-if="statusStyleOf(order).step >= 0"
                                            class="order-journey-pop"
                                            :title="journeyTitle(order)"
                                            :label="`Andamento do pedido: ${journeyTitle(order)}`"
                                            :width="230">
                                            <template #trigger>
                                                <span class="order-journey" aria-hidden="true">
                                                    <i v-for="(stepName, index) in ORDER_JOURNEY" :key="stepName"
                                                        :class="{'is-done': index <= statusStyleOf(order).step}"></i>
                                                </span>
                                            </template>

                                            <ol class="journey-steps">
                                                <li v-for="(stepName, index) in ORDER_JOURNEY" :key="stepName"
                                                    :class="{
                                                        'is-done': index < statusStyleOf(order).step,
                                                        'is-current': index === statusStyleOf(order).step,
                                                    }">
                                                    <span class="journey-step-mark">
                                                        <fa v-if="index < statusStyleOf(order).step" icon="check" />
                                                        <template v-else>{{index + 1}}</template>
                                                    </span>
                                                    <span class="journey-step-name">{{stepName}}</span>
                                                    <span v-if="index === statusStyleOf(order).step && stageHintOf(order)" class="journey-step-now">
                                                        {{stageHintOf(order)}}
                                                    </span>
                                                </li>
                                            </ol>
                                        </InfoPopoverCmp>
                                        <span v-else class="order-journey is-off" aria-hidden="true">
                                            <i v-for="stepName in ORDER_JOURNEY" :key="stepName"></i>
                                        </span>
                                        <fa :icon="statusStyleOf(order).icon" fixed-width class="order-tone-icon" />
                                        {{order.getOrderStatusEnumDescription()}}
                                    </td>
                                    <td class="text-center muven-td-with-buttons">
                                        <button type="button" 
                                            class="btn btn-default text-muted" 
                                            data-toggle="dropdown" >
                                            <fa icon="ellipsis-v" v-show="!order.loadingListAction" />
                                            <fa icon="sync-alt" spin v-show="order.loadingListAction" />
                                        </button>

                                        <div class="dropdown-menu dropdown-menu-right muven-action-menu">
                                            <router-link class="dropdown-item"
                                                :to="detailUrl(order)">
                                                <fa icon="file-alt" fixed-width class="muven-action-icon" />
                                                <span class="muven-action-label">Detalhes</span>
                                            </router-link>

                                            <div>
                                                <p class="dropdown-divider"></p>

                                                <span class="dropdown-item"
                                                    v-if="order.channel.isMuvenCart()"
                                                    @click="openFlowModal(order)">
                                                    <fa icon="hourglass-half" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Andamento do pedido</span>
                                                </span>

                                                <span class="dropdown-item"
                                                    v-if="order.canObtainInfoFromChannel()"
                                                    @click="forceRetrySync(order)">
                                                    <fa icon="cloud-download-alt" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Obter atualização do canal</span>
                                                </span>

                                                <span class="dropdown-item"
                                                    v-if="order.canForceSync()"
                                                    @click="forceChannelSync(order)">
                                                    <fa icon="cloud-upload-alt" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Forçar atualização no canal</span>
                                                </span>

                                                <span class="dropdown-item"
                                                    @click="showTrackingCodeModal(order)">
                                                    <fa icon="barcode" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Atualizar código de rastreio</span>
                                                </span>
                                            </div>

                                            <div v-if="order.canBeReimported()">
                                                <p class="dropdown-divider"></p>

                                                <span class="dropdown-item"
                                                    @click="openOrderReimportConfirmationModal(order)">
                                                    <fa icon="redo-alt" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Reimportar pedido</span>
                                                </span>
                                            </div>

                                            <div v-if="order.canBeDeleted()">
                                                <p class="dropdown-divider"></p>

                                                <span class="dropdown-item muven-action-danger"
                                                    @click="openConfirmationExclusionModal(order)">
                                                    <fa icon="trash-alt" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Excluir pedido</span>
                                                </span>
                                            </div>

                                            <div>
                                                <p class="dropdown-divider"></p>

                                                <span class="dropdown-item"
                                                    @click="showAuditQuickVisualization(order)">
                                                    <fa icon="search" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Auditoria rápida</span>
                                                </span>

                                                <a class="dropdown-item"
                                                    :href="getAuditUrl(order.id, EntityEnum.Order)"
                                                    target="_blank"
                                                    rel="noopener"
                                                    title="Abre em uma nova aba">
                                                    <fa icon="history" fixed-width class="muven-action-icon" />
                                                    <span class="muven-action-label">Ir para auditoria</span>
                                                    <fa icon="external-link-alt" class="muven-action-external" />
                                                </a>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>

                            <!-- Fechamento do livro-caixa: o total fica sob a coluna Valor e o rodapé gruda no pé da tela -->
                            <tfoot class="order-ledger" data-tour="order-ledger">
                                <tr>
                                    <td :colspan="columnsBeforeValue">
                                        <div class="ledger-figures ledger-fit">
                                            <span class="ledger-figure">
                                                <span class="ledger-figure-value">{{totals.validCount}}</span>
                                                <span class="ledger-figure-label">{{totals.validCount === 1 ? 'pedido' : 'pedidos'}}</span>
                                                <InfoPopoverCmp v-if="totals.canceledCount"
                                                    class="ledger-pop"
                                                    title="Cancelados"
                                                    :label="`${totals.canceledCount} ${totals.canceledCount === 1 ? 'pedido cancelado' : 'pedidos cancelados'}, fora do total`"
                                                    :width="240">
                                                    <template #trigger>
                                                        <span class="ledger-figure-aside">
                                                            + {{totals.canceledCount}} {{totals.canceledCount === 1 ? 'cancelado' : 'cancelados'}}
                                                        </span>
                                                    </template>
                                                    <p>
                                                        {{totals.canceledCount === 1 ? 'Soma' : 'Somam'}} <strong>{{currencyText(totals.canceledValue)}}</strong>
                                                        e {{totals.canceledCount === 1 ? 'fica' : 'ficam'}} fora do total, do ticket médio e da divisão por canal.
                                                    </p>
                                                </InfoPopoverCmp>
                                            </span>

                                            <span class="ledger-figure">
                                                <span class="ledger-figure-label">Ticket médio</span>
                                                <span class="ledger-figure-value">{{totals.average | currency}}</span>
                                            </span>

                                            <span class="ledger-figure" v-if="totals.discount > 0">
                                                <span class="ledger-figure-label">Descontos</span>
                                                <span class="ledger-figure-value">−&nbsp;{{totals.discount | currency}}</span>
                                            </span>
                                        </div>
                                    </td>
                                    <td class="value-col ledger-total">
                                        <InfoPopoverCmp class="ledger-pop"
                                            :title="showPaginationButton ? 'Total parcial' : 'Total vendido'"
                                            :label="`Total: ${currencyText(totals.gross)}${showPaginationButton ? ', parcial' : ''}`"
                                            :width="250">
                                            <template #trigger>
                                                <transition name="ledger-fade" mode="out-in">
                                                    <span :key="totals.gross">{{totals.gross | currency}}<span v-if="showPaginationButton" class="ledger-plus">+</span></span>
                                                </transition>
                                            </template>
                                            <p>Soma dos pedidos sem frete e sem cancelados.</p>
                                            <p v-if="showPaginationButton">
                                                Cobre só os pedidos já carregados: há mais nas próximas páginas.
                                                Use <strong>Mais resultados</strong> para incluir os demais.
                                            </p>
                                        </InfoPopoverCmp>
                                    </td>
                                    <td :colspan="columnsAfterValue" class="ledger-scope">
                                        <div class="ledger-fit">
                                            <InfoPopoverCmp v-if="channelShares.length > 1"
                                                class="ledger-pop ledger-channels-pop"
                                                title="Vendas por canal"
                                                :label="channelSharesLabel"
                                                :width="290">
                                                <template #trigger>
                                                    <span class="ledger-channels" aria-hidden="true">
                                                        <span class="ledger-bar">
                                                            <span v-for="c in channelShares" :key="c.key"
                                                                class="ledger-bar-segment"
                                                                :style="{flexGrow: c.share, '--channel-color': c.color}"></span>
                                                        </span>
                                                        <span v-for="c in channelShares" :key="`l-${c.key}`" class="ledger-channel">
                                                            <span class="ledger-swatch" :style="{'--channel-color': c.color}"></span>
                                                            <ChannelLogoCmp :channel="c.channel" :size="16" :show-name="false" />
                                                            <span class="ledger-channel-share">{{c.percent}}</span>
                                                        </span>
                                                    </span>
                                                </template>

                                                <ul class="share-rows">
                                                    <li v-for="c in channelShares" :key="`p-${c.key}`" class="share-row">
                                                        <span class="share-row-swatch" :style="{'--channel-color': c.color}"></span>
                                                        <span class="share-row-name">{{c.channel.name}}</span>
                                                        <span class="share-row-percent">{{c.percent}}</span>
                                                        <span class="share-row-track">
                                                            <span class="share-row-fill" :style="{width: `${Math.max(c.share * 100, 2)}%`, '--channel-color': c.color}"></span>
                                                        </span>
                                                        <span class="share-row-detail">
                                                            {{currencyText(c.value)}} · {{c.count}} {{c.count === 1 ? 'pedido' : 'pedidos'}}
                                                        </span>
                                                    </li>
                                                </ul>
                                                <p class="share-note">
                                                    Sem frete e sem cancelados<template v-if="showPaginationButton">, só dos pedidos já carregados</template>.
                                                </p>
                                            </InfoPopoverCmp>
                                            <span class="ledger-scope-sub">sem frete · {{orders.length}} {{orders.length === 1 ? 'pedido carregado' : 'pedidos carregados'}}</span>
                                        </div>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div v-if="openDayGroup" class="timeline-continues"></div>

                    <p v-if="openDayGroup" class="timeline-open-day-note">
                        <span v-if="openDayLabel">O dia {{openDayLabel}} pode continuar nas próximas páginas.</span>
                        <span v-else>A lista pode continuar nas próximas páginas.</span>
                    </p>
                </div>

                <div v-if="showPaginationButton" class="text-center">
                    <button class="btn btn-outline-secondary"
                        @click="getMoreOrders()"
                        :disabled="loadingNextPage">
                        <span v-show="!loadingNextPage">
                            Mais resultados<span v-if="openDayLabel" class="pagination-day-hint"> ({{openDayLabel}} em diante)</span>
                        </span>
                        <fa v-show="loadingNextPage" icon="sync-alt" spin/>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {cloneDeep} from 'lodash'
import orderService from '@/order/services/OrderService'
import {PaginationModel} from '@/utils/models/PaginationModel'
import {dateTimeFilter, timeFilter, dayMonthFilter} from '@/utils/filters/DateTimeFilter'
import {groupOrdersByDay} from '@/order/normalizers/OrderTimelineNormalizer'
import orderListPreferencesManager, {VIEW_MODE_TIMELINE, VIEW_MODE_FLAT} from '@/order/managers/OrderListPreferencesManager'
import {OrderListFilterModel} from '@/order/models/OrderListFilterModel'
import modalService from '@/utils/services/ModalService'
import { OrderModel } from '@/order/models/OrderModel'
import { OrderTrackingCodeModel } from '@/order/models/OrderTrackingCodeModel'
import channelService from '@/channel/services/ChannelService'
import FilterInformationCmp from '@/utils/components/FilterInformationCmp'
import { EntityEnum } from '@c2b/muven-commons'
import GetAuditUrl from '@/utils/mixins/GetAuditUrl'
import {infoOrPlaceholder} from '@/utils/filters/InformationOrPlaceholderFilter'
import SelectWrapperCmp from '@/utils/components/SelectWrapperCmp'
import OrderFlowCmp from "@/order/components/OrderFlowCmp"
import { StoreModel } from '@/store/models/StoreModel'
import productService from '@/product/services/ProductService'
import { ProductModel } from '../../product/models/ProductModel'
import ConfirmationExclusionModalCmp from '@/utils/components/ConfirmationExclusionModalCmp'
import OrderFakeCmp from '@/order/components/OrderFakeCmp.vue'
import AuditListQuickVisualizationCmp from '@/audit/components/AuditListQuickVisualizationCmp'
import { OrderImportSpecificModel } from '@/order/models/OrderImportSpecificModel'
import ChannelLogoCmp from '@/channel/components/ChannelLogoCmp'
import { getChannelBrand, getChannelGlow } from '@/channel/models/ChannelBrand'
import CopyButtonCmp from '@/utils/components/CopyButtonCmp.vue'
import InfoPopoverCmp from '@/utils/components/InfoPopoverCmp.vue'
import { getOrderStatusStyle, getOrderStageHint, ORDER_JOURNEY } from '@/order/utils/OrderStatusStyle'
import { formatPersonName } from '@/utils/helpers/NameFormatter'
import { summarizeOrders } from '@/order/utils/OrderListTotals'
import { simpleCurrencyFilter } from '@/utils/filters/SimpleCurrencyFilter'
import FeatureTourCmp from '@/utils/components/FeatureTourCmp.vue'
import { ORDER_LIST_TOUR } from '@/utils/tours/FeatureTours'

function sharePercent(share) {
    const p = share * 100

    return p > 0 && p < 1 ? '<1%' : `${Math.round(p)}%`
}

const ID_TRACKING_CODE_MODAL = '#muven-order-tracking-code-modal'
const ID_ORDER_REIMPORT_MODAL = '#muven-order-reimport-confirmation-modal'    
const ID_ORDER_IMPORT_SPECIFIC_MODAL = '#muven-order-import-specific-modal'
const ID_FILTER_MODAL = '#muven-order-list-filter-modal'
const FILTER_BY_BACKEND_INFO = 'filter_by_backend_info'
const FILTER_BY_CHANNEL_INFO = 'filter_by_channel_info'
const FILTER_BY_TRACKING_CODE = 'filter_by_tracking_code'
const FILTER_ADVANCED = 'filter_advanced'

export default {
    name: 'OrderListView',
    components: {
        InfoPopoverCmp,
        FilterInformationCmp,
        SelectWrapperCmp,
        OrderFlowCmp,
        ConfirmationExclusionModalCmp,
        OrderFakeCmp,
        AuditListQuickVisualizationCmp,
        ChannelLogoCmp,
        CopyButtonCmp,
        FeatureTourCmp,
    },
    mixins: [
        GetAuditUrl,
    ],
    filters: {
        infoOrPlaceholder,
    },
    data() {
        return {
            tour: ORDER_LIST_TOUR,
            EntityEnum,
            ORDER_JOURNEY,
            VIEW_MODE_TIMELINE,
            VIEW_MODE_FLAT,
            viewMode: orderListPreferencesManager.getViewMode(),
            loading: true,
            loadingNextPage: false,
            loadingFilter: false,
            loadingUpdatingOrderTrackingCode: false,
            showPaginationButton: true,
            reimportingOrder: false,
            importingOrderSpecific: false,
            loadingFakeOrderModalInfo: false,
            pagination: new PaginationModel(),
            orders: [],
            orderSelectedTrackingCode: new OrderModel(),
            orderImportSpecific: new OrderImportSpecificModel(),
            orderTrackingCode: new OrderTrackingCodeModel(),
            statusListOptions: OrderListFilterModel.statusList(),
            statusTrackingCodeListOptions: OrderListFilterModel.statusTrackingCodeList(),
            statusIdSourceListOptions: OrderListFilterModel.statusIdSourceList(),
            channelListOptions: [], // get from channelService.getChannels()
            orderFilter: new OrderListFilterModel(),
            orderFilterSubmitted: new OrderListFilterModel(),
            chosenOrderReimport: new OrderModel(),
            filterOptions: [
                {
                    enum: FILTER_ADVANCED,
                    description: 'Geral',
                    active: true,
                },
                {
                    enum: FILTER_BY_BACKEND_INFO,
                    description: 'Retaguarda',
                    active: false,
                },
                {
                    enum: FILTER_BY_CHANNEL_INFO,
                    description: 'Canal',
                    active: false,
                },
                {
                    enum: FILTER_BY_TRACKING_CODE,
                    description: 'Rastreio',
                    active: false,
                },
            ],
            filterOptionSelected: {
                enum: FILTER_ADVANCED,
                description: 'Geral',
                active: true,
            },
            orderFilterProperties: [
                {
                    type: 'daterange',
                    label: 'Período de compra',
                    property: 'date',
                },
                {
                    type: 'daterange',
                    label: 'Período de criação no Muven',
                    property: 'dateCreatedAtMuven',
                },
                {
                    type: 'string',
                    label: 'Cliente',
                    property: 'customer',
                },
                {
                    type: 'string',
                    label: 'Chave de acesso',
                    property: 'accessKey',
                },
                {
                    type: 'list',
                    label: 'Canal',
                    property: 'channelList',
                    propertyInList: 'name',
                },
                {
                    type: 'list',
                    label: 'Status',
                    property: 'statusList',
                    propertyInList: 'description',
                },
                {
                    type: 'string',
                    label: 'Código de rastreio',
                    property: 'trackingCode',
                },
                {
                    type: 'enumwrapped',
                    label: 'Status do código de rastreio',
                    property: 'statusTrackingCode',
                },
                {
                    type: 'string',
                    label: 'Id na retaguarda',
                    property: 'idSource',
                },
                {
                    type: 'enumwrapped',
                    label: 'Status da importação na retaguarda',
                    property: 'statusIdSource',
                },
                {
                    type: 'string',
                    label: 'Id no canal',
                    property: 'idExternalChannel',
                },
                {
                    type: 'list',
                    label: 'Loja',
                    property: 'storeList',
                    propertyInList: 'name',
                },
                {
                    type: 'list',
                    label: 'Pedido com o produto',
                    property: 'productIdList',
                    propertyInList: 'name',
                },
            ]
        }
    },
    async mounted() {
        try {
            const querystring = this.$route.query
            this.orderFilter = new OrderListFilterModel(querystring)

            for (const prod of this.orderFilter.productIdList) {
                const p = ProductModel.normalizeV1(await productService.getProductById(prod.id), this.subscriber)

                if (p.isSku && p.hasAttributeTypeGrid()) {
                    prod.name = `${p.name} (${p.getVariationsStringified()})`
                } else {
                    prod.name = p.name
                }
            }

            this.orders = await orderService.getOrders(this.pagination.actual(), this.orderFilter)
            this.channelListOptions = await channelService.getChannels()
            this.pagination.next()

            if (querystring) {                
                this.orderFilterSubmitted = cloneDeep(this.orderFilter)
            }
        } catch(e) {
            this.$floatingAlert.error({
                message: e.message,
            })
        }

        this.loading = false
    },
    methods: {
        changeViewMode(mode) {
            this.viewMode = mode
            orderListPreferencesManager.setViewMode(mode)
        },
        // Na timeline o dia já está no cabeçalho do grupo, então a célula mostra só a
        // hora. Sem cabeçalho essa informação se perderia, e a data completa volta.
        orderDateLabel(order) {
            return this.isTimeline ? timeFilter(order.createdAt) : dateTimeFilter(order.createdAt)
        },
        orderDateTitle(order) {
            return this.isTimeline ? dateTimeFilter(order.createdAt) : null
        },
        groupCountLabel(group) {
            // Num dia ainda em aberto o plural vale sempre: existe pelo menos mais um possível.
            if (group.isOpen) {
                return `${group.orders.length}+ pedidos`
            }

            return `${group.orders.length} ${group.orders.length === 1 ? 'pedido' : 'pedidos'}`
        },
        // A linha toda leva ao detalhe, menos quando o clique foi num controle dela
        // (copiar, menu, link) ou quando a pessoa estava selecionando texto.
        openOrder(order, event) {
            if (event && event.target && event.target.closest && event.target.closest('a, button, .dropdown-menu, .muven-td-with-buttons')) {
                return
            }

            const selection = window.getSelection && window.getSelection()

            if (selection && selection.toString()) {
                return
            }

            this.$router.push(this.detailUrl(order))
        },
        importTone(order) {
            if (order.wasCanceledBeforeBackend()) {
                return 'is-muted'
            }

            return order.hasIdSource() ? 'is-muted' : 'is-warning'
        },
        stageHintOf(order) {
            return getOrderStageHint(this.statusStyleOf(order).stage)
        },
        journeyTitle(order) {
            const step = this.statusStyleOf(order).step

            if (step < 0) {
                return null
            }

            return `Etapa ${step + 1} de ${ORDER_JOURNEY.length} · ${ORDER_JOURNEY[step]}`
        },
        currencyText(n) {
            return simpleCurrencyFilter(n)
        },
        orderValueTitle(order) {
            const lines = ['Produtos + acréscimos - descontos. Não inclui frete.']

            if (order.hasDiscount()) {
                const coupon = order.discountCoupon ? ` (cupom ${order.discountCoupon})` : ''
                lines.push(`Desconto: ${simpleCurrencyFilter(order.discountValue)}${coupon}`)
            }

            if (order.isCanceled()) {
                lines.push('Cancelado: fica fora das somas.')
            }

            return lines.join('\n')
        },
        async getMoreOrders() {
            try {
                this.loadingNextPage = true
                const newOrders = await orderService.getOrders(this.pagination.actual(), this.orderFilter)
                this.orders.push(...newOrders)

                if (!newOrders.length) {
                    this.showPaginationButton = false
                }

                this.pagination.next()
            } catch(e) {
               this.$floatingAlert.error({
                   message: e.message,
               })               
            }
            
            this.loadingNextPage = false
        },
        async getOrdersWithFilter(filterList) {
            this.showPaginationButton = true
            
            if (filterList.hasTooSpecificFilter()) {
                filterList.clearStatusList()
            }

            this.orderFilterSubmitted = cloneDeep(filterList)
            this.pagination.reset()
            this.loadingFilter = true
            try {
                this.orders = await orderService.getOrders(this.pagination.actual(), filterList)
                this.closeFilterModal()
                this.pagination.next()
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loadingFilter = false
        },
        updateFilterOption(filterOption) {
            this.filterOptions.forEach((fo) => {
                fo.active = false
            })

            this.filterOptionSelected = Object.assign(filterOption, {active: true})            
            this.orderFilter = new OrderListFilterModel()

            if (!this.isFilterOptionAdvanced) {
                this.orderFilter.clearStatusList()
            } 
        },
        openConfirmationExclusionModal(order) {
            const info = {
                id: order.id,
                title: 'Exclusão de pedido',
                message: `Tem certeza que deseja excluir o pedido <span style="font-weight: 600; color: var(--text-soft); text-transform: uppercase; letter-spacing: 1px;">${order.idExternalChannel}</span>?`,
            }

            this.$refs.confirmationExclusionModal.open(info)
        },
        async reimportOrder(order) {
            this.reimportingOrder = true;

            try {
                await orderService.reimport(order.id)

                this.$floatingAlert.info({
                    message: `Solicitação de reimportação do pedido <b>${order.idExternalChannel}</b> no canal <b>${order.channel.name}</b> registrada com sucesso.`,
                    timeVisible: 10000,
                })

                this.closeOrderReimportConfirmationModal()
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            this.reimportingOrder = false;
        },
        async importSpecificOrder(specificOrder) {
            this.importingOrderSpecific = true;

            try {
                await orderService.importSpecific(specificOrder.channel.id, specificOrder.idExternalChannel)

                this.$floatingAlert.info({
                    message: `Solicitação de importação do pedido <b>${specificOrder.idExternalChannel}</b> no canal <b>${specificOrder.channel.name}</b> registrada com sucesso.`,
                    timeVisible: 10000,
                })

                this.closeOrderImportSpecificModal()
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            this.importingOrderSpecific = false;
        },
        openOrderReimportConfirmationModal(order) {
            this.chosenOrderReimport = cloneDeep(order);
            modalService.show(ID_ORDER_REIMPORT_MODAL);
        },
        closeOrderReimportConfirmationModal() {
            modalService.hide(ID_ORDER_REIMPORT_MODAL);
        },
        openOrderImportSpecificModal() {
            modalService.show(ID_ORDER_IMPORT_SPECIFIC_MODAL);
        },
        closeOrderImportSpecificModal() {
            modalService.hide(ID_ORDER_IMPORT_SPECIFIC_MODAL);
        },
        async confirmExclusionHandler({id}) {
            try {
                await orderService.deleteOrder(id)

                for (let i = 0; i < this.orders.length; i++) {
                    if (this.orders[i].id === id) {
                        this.$floatingAlert.success({
                            message: `Pedido <span style="font-weight: 600; color: var(--text-soft); text-transform: uppercase; letter-spacing: 1px;">${this.orders[i].idExternalChannel}</span> excluído com sucesso.`,
                        })
                        this.orders.splice(i, 1)
                        break
                    }
                }
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.$refs.confirmationExclusionModal.close()
        },
        async showFilterModal() {
            await modalService.show(ID_FILTER_MODAL)
        },
        async closeFilterModal() {
            await modalService.hide(ID_FILTER_MODAL)
        },
        async showFakeOrderModal() {
            this.loadingFakeOrderModalInfo = true;
            await this.$refs.orderFakeModal.showModal()
            this.loadingFakeOrderModalInfo = false;
        },
        async showTrackingCodeModal(order) {
            this.orderSelectedTrackingCode = order;
            this.orderTrackingCode = new OrderTrackingCodeModel()
            await modalService.show(ID_TRACKING_CODE_MODAL)
        },
        async closeTrackingCodeModal() {
            await modalService.hide(ID_TRACKING_CODE_MODAL)
        },
        async updateOrderTrackingCode(order, trackingCode) {
            this.loadingUpdatingOrderTrackingCode = true

            try {
                await orderService.updateTrackingCode(order.id, trackingCode)
                this.$floatingAlert.success({
                    message: 'Código de rastreio atualizado com sucesso!',
                })
                this.closeTrackingCodeModal()
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loadingUpdatingOrderTrackingCode = false
        },
        async forceRetrySync(order) {
            order.loadingListAction = true

            try {
                const info = await orderService.retrySync(order.id)
                
                this.$floatingAlert.info({
                    message: info.message,
                })
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            order.loadingListAction = false
        },
        async forceChannelSync(order) {
            order.loadingListAction = true

            try {
                const info = await orderService.forceChannelSync(order.id)
                
                this.$floatingAlert.info({
                    message: info.message,
                })
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            order.loadingListAction = false
        },
        handleOrderFakeSubmission(order) {
            this.orders.unshift(order)
        },
        goto(order) {
            this.$router.push(`/pedidos/${order.id}`)
        },
        clearFilter() {
            this.orderFilter = new OrderListFilterModel()
        },
        handleOrderStatusListRemoval() {
            this.orderFilter.clearStatusList()
        },
        async openFlowModal(order) {
            await this.$refs.orderFlow.openModal(order.id);
        },
        statusChangedHandler(info) {
            for (const order of this.orders) {
                if (order.isEqual(info)) {
                    order.setStatus(info.status);
                    break;
                }
            }
        },
        handleOrderStoreListRemoval() {
            this.orderFilter.storeList = []
        },
        async showAuditQuickVisualization(order) {
            order.loadingListAction = true

            await this.$refs.auditListQuickVisualizationModal.showModal({
                idEntity: order.id,
                entityList: EntityEnum.Order,
                getDescription: () => `Pedido: ${order.idExternalChannel}`,
            });

            order.loadingListAction = false
        },
    },
    computed: {
        ...mapState({
            subscriber: state => state.subscriber,
            storeList: (state) => {
                return StoreModel.getStoreListWithIdAndName(state.storeList)
            },
        }),
        ...mapGetters([
            'hasMoreThanOneStore',
        ]),
        nothingToShow() {
            return !this.loading && !this.orders.length
        },
        isTimeline() {
            return this.viewMode === VIEW_MODE_TIMELINE
        },
        orderGroups() {
            return groupOrdersByDay(this.orders, this.showPaginationButton)
        },
        // Na lista clássica tudo cai num grupo único e sem cabeçalho. É o que permite
        // manter um só v-for de linhas atendendo aos dois modos.
        displayGroups() {
            if (this.isTimeline) {
                return this.orderGroups
            }

            return [{key: 'all', orders: this.orders, isHeaderless: true}]
        },
        // O último dia da lista é o único cuja contagem ainda pode crescer na paginação.
        // Sem contagens por dia na tela, a ressalva não tem o que qualificar.
        openDayGroup() {
            if (!this.isTimeline) {
                return null
            }

            const lastGroup = this.orderGroups[this.orderGroups.length - 1]

            return lastGroup && lastGroup.isOpen ? lastGroup : null
        },
        openDayLabel() {
            return this.openDayGroup ? dayMonthFilter(this.openDayGroup.date) : undefined
        },
        // Colunas antes e depois de Valor, para as linhas de soma (cabeçalho do dia e
        // rodapé) colocarem o número exatamente sob os valores dos pedidos.
        columnsBeforeValue() {
            return this.hasMoreThanOneStore ? 4 : 3
        },
        columnsAfterValue() {
            return 3
        },
        totals() {
            return summarizeOrders(this.orders)
        },
        // Soma de cada dia, sem cancelados: a mesma regra do total do rodapé.
        groupGross() {
            const map = {}

            for (const group of this.orderGroups) {
                map[group.key] = summarizeOrders(group.orders).gross
            }

            return map
        },
        // Cada canal na cor da sua marca. Marca sem cor aproveitável (preta, token
        // de tema) cai no tom neutro; o mesmo canal repetido (duas contas) clareia
        // a cada repetição para os segmentos não se fundirem.
        channelShares() {
            if (this.totals.gross <= 0) {
                return []
            }

            const seen = {}

            return this.totals.byChannel.map((c) => {
                const base = getChannelGlow(getChannelBrand(c.channel && c.channel.enumChannel)) || 'var(--text-soft)'
                const repeat = seen[base] || 0
                seen[base] = repeat + 1

                return Object.assign({}, c, {
                    color: repeat ? `color-mix(in srgb, ${base} ${Math.max(100 - repeat * 40, 25)}%, transparent)` : base,
                    percent: sharePercent(c.share),
                })
            })
        },
        channelSharesLabel() {
            return 'Participação por canal: ' + this.channelShares
                .map((c) => `${c.channel.name} ${c.percent}`)
                .join(', ')
        },
        hasAccessToMoreThanOneChannel() {
            return this.channelListOptions.length > 1
        },
        isFilterOptionByBackendInfo() {
            return this.filterOptionSelected.enum === FILTER_BY_BACKEND_INFO
        },
        isFilterOptionByChannelInfo() {
            return this.filterOptionSelected.enum === FILTER_BY_CHANNEL_INFO
        },
        isFilterOptionByTrackingCodeInfo() {
            return this.filterOptionSelected.enum === FILTER_BY_TRACKING_CODE
        },
        isFilterOptionAdvanced() {
            return this.filterOptionSelected.enum === FILTER_ADVANCED
        },
        detailUrl() {
            return (order) => {
                return `/pedidos/${order.id}`
            }
        },
        customerName() {
            return (order) => {
                const name = order.customer.fullName() || order.customer.corporateName || ''
                const nameSplit = name.trim().split(/\s+/)
                const first = nameSplit[0]
                const last = nameSplit[nameSplit.length-1]

                if (first === last) {
                    return formatPersonName(first)
                }

                return formatPersonName(`${first} ${last}`)
            }
        },
        // Ícone, tom e etapa de cada pedido, calculados uma vez por pedido (a
        // célula consulta várias vezes). Recalcula quando a lista ou um status muda.
        statusStyleOf() {
            const cache = {}

            for (const order of this.orders) {
                cache[`${order.id}:${order.status}`] = getOrderStatusStyle(order)
            }

            return (order) => cache[`${order.id}:${order.status}`] || getOrderStatusStyle(order)
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/utils/styles/constants.scss';

$rail-offset: 30px;   // recuo da tabela, abrindo espaço para a trilha
$rail-width: 2px;
$rail-left: -22px;    // x da trilha em relação à primeira célula da linha
// Trilha, regua e marcador sao decoracao, entao usam o roxo de preenchimento
// (--brand-fill), e nao o de texto (--brand): no claro e o mesmo #89138a, no escuro
// o de texto clareia para ficar legivel e, em traco de 2px numa lista longa, pesava.
$rail-color: color-mix(in srgb, var(--brand-fill) 30%, transparent);
$dot-size: 12px;
$dot-left: -27px;     // $rail-left - ($dot-size - $rail-width) / 2, centraliza o marcador na trilha

// Numa lista longa o cinza tem o mesmo peso das bordas da tabela e a separação
// some. A cor da marca resolve, e a régua saturada no topo do cabeçalho é o que
// o olho realmente captura ao varrer rolando.
$day-rule: color-mix(in srgb, var(--brand-fill) 55%, transparent);
$day-tint: color-mix(in srgb, var(--brand-fill) 7%, transparent);
$day-tint-today: color-mix(in srgb, var(--brand-fill) 13%, transparent);

/*
 * Toolbar da listagem: ações à esquerda, alternador de formato ancorado na borda
 * direita da tabela. Mesma linguagem do controle de ordenação de Produtos - o
 * bloco pertence à tabela abaixo, então repete a borda (#dee2e6) e o raio de 2px
 * dela em vez de ficar solto ao lado dos botões.
 */
.order-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
}

.order-toolbar-actions {
    display: flex;
    align-items: center;
}

.order-view-mode {
    display: flex;
    align-items: stretch;
    // mantem o bloco ancorado à direita mesmo quando a toolbar quebra em duas linhas
    margin-left: auto;
    border: 1px solid var(--border);
    border-radius: 2px;
    background-color: var(--surface-1);
    overflow: hidden;
    transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;

    &:hover {
        border-color: var(--tint-brand-border);
    }

    &:focus-within {
        border-color: var(--brand-soft);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--brand-soft) 12%, transparent);
    }
}

.order-view-mode-caption {
    display: flex;
    align-items: center;
    padding: 0 12px;
    background-color: var(--surface-2);
    border-right: 1px solid var(--border);
    color: var(--text-muted);
    font-size: .8em;
    font-weight: 600;
    letter-spacing: .5px;
    text-transform: uppercase;
    white-space: nowrap;
}

// Botão próprio, sem a classe .btn: o common.scss aplica uppercase e paddings
// dela que não cabem num controle deste tamanho.
.order-view-mode-option {
    appearance: none;
    -webkit-appearance: none;
    height: 34px;
    padding: 0 14px;
    border: 0;
    background-color: transparent;
    color: var(--text-soft);
    font-size: .95em;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color .15s ease-in-out, color .15s ease-in-out;

    &:hover:not(.is-active) {
        background-color: color-mix(in srgb, #{$color-primary} 7%, transparent);
    }

    &:focus {
        outline: 0;
    }

    &.is-active {
        background-color: var(--brand-fill);
        color: var(--text-on-brand);
        font-weight: 600;
        cursor: default;
    }
}

// Toda a trilha vive aninhada aqui: na lista clássica as linhas continuam com a
// classe .order-row (para o zebrado e o hover), e regras soltas fariam elas
// desenharem segmentos de trilha sem trilha nenhuma em volta.
.order-timeline {
    position: relative;
    padding-left: $rail-offset;

    // O marcador e a trilha ficam à esquerda da tabela, fora dela. O overflow do
    // .table-responsive os recortaria, então ele só entra em ação no breakpoint
    // em que a trilha deixa de ser exibida.
    .table-responsive {
        overflow: visible;
    }

    // A trilha é desenhada por linha, e não como um elemento único de altura total.
    // É o que permite tracejar apenas o trecho do dia em aberto sem precisar medir
    // em JS onde o último marcador caiu.
    .timeline-day td:first-child,
    .order-row td:first-child,
    .order-ledger td:first-child {
        position: relative;

        &::before {
            content: '';
            position: absolute;
            left: $rail-left;
            top: 0;
            bottom: 0;
            width: $rail-width;
            background-color: $rail-color;
        }
    }

    // O rodapé continua sticky: sticky também é posicionado, então segura o ::before.
    .order-ledger td:first-child {
        position: sticky;
    }

    // Num dia em aberto a trilha vira tracejada: o dia não fechou e pode continuar
    // nas próximas páginas.
    .timeline-day.is-open-day td:first-child::before,
    .order-row.is-open-day td:first-child::before,
    &.has-open-day .order-ledger td:first-child::before {
        background-color: transparent;
        border-left: $rail-width dashed $rail-color;
    }
}

.timeline-day {
    td {
        padding-top: 20px;
        padding-bottom: 7px;
        border-top: 2px solid $day-rule;
        background-color: $day-tint;
        white-space: nowrap;

        // O marcador vai no ::after para pintar por cima do segmento de trilha,
        // que ocupa o ::before da mesma célula. Só na primeira: o cabeçalho tem
        // três células (rótulo, subtotal e resto).
        &:first-child::after {
            content: '';
            position: absolute;
            left: $dot-left;
            top: 50%;
            width: $dot-size;
            height: $dot-size;
            margin-top: -6px;
            border: 2px solid var(--brand-fill);
            border-radius: 50%;
            background-color: var(--surface-1);
        }
    }

    // O cabeçalho é uma divisória, não uma linha clicável: o hover da tabela não
    // pode apagar a cor dele.
    &:hover td {
        background-color: $day-tint;
    }

    // O dia corrente é o único com a régua na cor cheia e o fundo mais carregado.
    &.is-today td {
        border-top-color: var(--brand-fill);
        background-color: $day-tint-today;
    }

    &.is-today:hover td {
        background-color: $day-tint-today;
    }

    &.is-today td:first-child::after {
        background-color: var(--brand-fill);
        box-shadow: 0 0 0 4px color-mix(in srgb, var(--brand-fill) 15%, transparent);
    }

    &.is-open-day td:first-child::after {
        border-style: dashed;
    }
}

// O primeiro grupo dispensa a régua: o thead já tem a própria borda logo acima,
// e as duas juntas viram linha dupla.
tbody:first-of-type .timeline-day td {
    border-top: 0;
}

// Rabicho tracejado abaixo da tabela, terminando num círculo vazado: a leitura
// visual de que a lista continua.
.timeline-continues {
    position: relative;
    height: 26px;

    &::before {
        content: '';
        position: absolute;
        left: $rail-left;
        top: 0;
        bottom: 8px;
        border-left: $rail-width dashed $rail-color;
    }

    &::after {
        content: '';
        position: absolute;
        left: $dot-left + 2px;
        bottom: 0;
        width: $dot-size - 4px;
        height: $dot-size - 4px;
        border: $rail-width solid $rail-color;
        border-radius: 50%;
    }
}

.timeline-day-label {
    text-transform: uppercase;
    letter-spacing: 1.5px;
    font-weight: 700;
    font-size: .85em;
    color: $color-primary;
}

.timeline-day-count {
    margin-left: 14px;
    // Um tom abaixo do rótulo: a data é o que se procura, a contagem é o apoio.
    // Não desce mais que isto, ou o contraste cai abaixo do mínimo AA.
    color: color-mix(in srgb, #{$color-primary} 85%, transparent);
    font-size: .85em;
    letter-spacing: 1px;

    // O "+" indica contagem parcial, então não pode passar batido.
    &.is-open {
        color: $color-primary;
        font-weight: 600;
        cursor: help;
    }
}

// Subtotal do dia: mesma vertical dos valores dos pedidos, no roxo do rótulo.
.timeline-day td.timeline-day-value {
    color: $color-primary;
    font-weight: 700;
    font-size: .95em;

    &.is-open {
        cursor: help;
    }
}

// Valor do pedido: alinhado à direita e com algarismos de largura fixa, para que
// as casas decimais formem coluna e a varredura vertical compare de relance.
// O respiro à direita separa do "Importação ret.", que é texto alinhado à esquerda.
.value-col {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    padding-right: 32px !important;
}

.order-value {
    color: var(--text-strong);
    font-weight: 600;
}

// Marcador de desconto: fica à esquerda do número e não muda a altura da linha.
// O detalhe (valor e cupom) está no title da célula.
.order-value-discount {
    margin-right: 6px;
    color: var(--text-muted);
    font-size: .75em;
    vertical-align: baseline;
}

// Cancelado: riscado e esmaecido, a pista visual de que não entra nas somas.
.value-col.is-canceled {
    .order-value {
        color: var(--text-muted);
        font-weight: 400;
        text-decoration: line-through;
    }
}

// A linha inteira abre o detalhe.
.order-row {
    cursor: pointer;
}

// O nome é o link "de verdade" da linha: cor de texto, sublinhado só no hover.
.order-customer-link {
    color: var(--text);

    &:hover,
    &:focus-visible {
        color: var(--brand);
        text-decoration: underline;
        text-underline-offset: 3px;
    }
}

// Coluna "Pedido": tile do canal ocupando a altura das duas linhas, o id em
// cima (o que se procura) e o nome do canal embaixo, apagado. O botão de copiar
// tem lugar reservado e só aparece com a linha em foco/hover.
.order-ident {
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
}

// O copiar acompanha a linha do id (a de cima), não o meio da pilha.
.order-ident .order-id-copy {
    align-self: flex-start;
    margin-top: -3px;
}

.order-ident-logo {
    gap: 10px;

    ::v-deep .channel-logo-name {
        color: var(--text);
        font-weight: 500;
        font-variant-numeric: tabular-nums;
        letter-spacing: .2px;
    }

    ::v-deep .channel-logo-caption {
        font-size: .82em;
        max-width: 220px;
    }
}

.order-id-copy {
    min-width: 24px;
    height: 24px;
    margin: -4px 0 -4px 4px;
    opacity: 0;
    transition: opacity .15s ease, color .15s, background-color .15s, border-color .15s;

    .order-row:hover &,
    .order-row:focus-within &,
    &.is-copied {
        opacity: 1;
    }

    // Em tela de toque não existe hover: o botão fica sempre à mostra.
    @media (hover: none) and (pointer: coarse) {
        opacity: 1;
    }
}

/*
 * Calmo no normal, alto na exceção. O estado esperado (importado, aprovado,
 * faturado...) fica em texto comum com ícone apagado; só o que pede atenção
 * ganha a cor do tom e peso.
 */
.order-import,
.order-status {
    white-space: nowrap;
}

.order-tone-icon {
    margin-right: 4px;
    font-size: .9em;
    color: var(--text-muted);
}

.order-import.is-muted {
    color: var(--text-muted);
}

.order-import.is-warning,
.order-status.is-warning {
    color: var(--warning-fg);
    font-weight: 600;

    .order-tone-icon {
        color: inherit;
    }
}

.order-status.is-danger {
    color: var(--danger-fg);
    font-weight: 600;

    .order-tone-icon {
        color: inherit;
    }
}

.order-status.is-info {
    color: var(--info-fg);

    .order-tone-icon {
        color: inherit;
    }
}

// Mini jornada: Pendente > Aprovado > Faturado > Enviado > Entregue, a mesma do
// detalhe. Fora da jornada (cancelado, erro) o espaço fica, para o texto alinhar.
.order-journey-pop {
    margin-right: 8px;
}

.order-journey {
    display: inline-flex;
    gap: 2px;
    vertical-align: middle;

    i {
        width: 3px;
        height: 10px;
        border-radius: 1px;
        background-color: var(--border);
    }

    i.is-done {
        background-color: var(--brand-fill);
    }

    &.is-off {
        visibility: hidden;
        margin-right: 8px;
        padding: 0 2px;
    }
}

// Etapas no popover da barrinha: feitas com check, a atual em destaque
.journey-steps {
    margin: 8px 0 0;
    padding: 0;
    list-style: none;

    li {
        position: relative;
        display: grid;
        grid-template-columns: 18px 1fr;
        column-gap: 8px;
        align-items: center;
        padding: 3px 0;
        color: var(--text-on-tooltip-muted);

        // Fio ligando uma etapa à seguinte
        &:not(:last-child)::after {
            content: '';
            position: absolute;
            top: 21px;
            bottom: -5px;
            left: 8px;
            width: 2px;
            border-radius: 1px;
            background: color-mix(in srgb, var(--text-on-tooltip) 18%, transparent);
        }

        &.is-done {
            color: var(--text-on-tooltip-soft);

            &::after {
                background: var(--brand-fill);
            }
        }

        &.is-current {
            color: var(--text-on-tooltip);
            font-weight: 700;
        }
    }
}

.journey-step-mark {
    display: grid;
    place-items: center;
    width: 18px;
    height: 18px;
    border: 1.5px solid color-mix(in srgb, var(--text-on-tooltip) 30%, transparent);
    border-radius: 50%;
    font-size: 9px;
    font-weight: 700;

    .is-done > & {
        border-color: var(--brand-fill);
        color: var(--text-on-brand);
        background: var(--brand-fill);
    }

    .is-current > & {
        border-color: var(--brand-fill);
        color: var(--text-on-brand);
        background: var(--brand-fill);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-fill) 35%, transparent);
    }
}

.journey-step-now {
    grid-column: 2;
    color: var(--text-on-tooltip-muted);
    font-size: 11px;
    font-weight: 500;
}

.ledger-plus {
    margin-left: 1px;
    font-size: .7em;
    vertical-align: .45em;
}

/*
 * Rodapé do livro-caixa. Faz parte da tabela, então herda bordas e o recuo da
 * timeline. Gruda no pé da tela enquanto se rola a lista, como a barra de status
 * de uma planilha, e para no lugar dele quando a tabela termina.
 */
.order-ledger td {
    position: sticky;
    bottom: 0;
    z-index: 2;
    // Mesmos recuos laterais do tbody (overrides.scss), para o texto alinhar com as linhas.
    padding: 12px 8px;
    border-top: 0;
    border-bottom: 1px solid var(--border);
    background-color: var(--surface-1);
    // O fio roxo de "total" vai como sombra interna: com border-collapse a borda
    // de uma célula sticky fica para trás quando ela flutua. A sombra externa só
    // aparece de fato quando o rodapé está sobre as linhas.
    box-shadow:
        inset 0 2px 0 var(--brand-fill),
        0 -8px 12px -10px color-mix(in srgb, var(--text-strong) 35%, transparent);
    vertical-align: middle;

    &:first-of-type {
        padding-left: 14px;
        border-left: 1px solid var(--border);
    }

    &:last-of-type {
        padding-right: 14px;
        border-right: 1px solid var(--border);
    }
}

.order-ledger tr:hover td {
    background-color: var(--surface-1);
}

.ledger-figures {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px 24px;
}

.ledger-figure {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    white-space: nowrap;
}

.ledger-figure-label {
    color: var(--text-muted);
    font-size: .75em;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
}

.ledger-figure-value {
    color: var(--text-strong);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.ledger-figure-aside {
    color: var(--text-muted);
    font-size: .85em;
    border-bottom: 1px dotted currentColor;
}

// Popovers do rodapé: o gatilho é o próprio número, sem o espaçamento do "?"
.ledger-pop ::v-deep .ipo-trigger.is-custom {
    padding: 0;
    font: inherit;
    letter-spacing: inherit;
}

// O conteúdo do rodapé não pode alargar colunas: a largura de cada coluna é
// decidida pelas linhas de pedido, e o rodapé se acomoda dentro dela.
.ledger-fit {
    width: 0;
    min-width: 100%;
}

.ledger-channels {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 3px;
}

.ledger-bar {
    display: flex;
    gap: 2px;
    width: 72px;
    height: 4px;
    border-radius: 2px;
    overflow: hidden;
}

.ledger-bar-segment {
    flex-basis: 0;
    min-width: 3px;
    background-color: var(--channel-color);
    transition: flex-grow .4s ease, background-color .2s ease;
}

.channel-filter-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.ledger-channel {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

// O resumo inteiro (barra e canais) é o gatilho do popover
.ledger-channels-pop {
    display: flex;

    .ledger-channels {
        margin-bottom: 0;
    }
}

.ledger-swatch {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--channel-color);
    // Marcas escuras (Nuvemshop, Flexy) somem no tema escuro sem um contorno
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--text-muted) 25%, transparent);
}

.ledger-channel-share {
    color: var(--text-soft);
    font-size: .85em;
    font-variant-numeric: tabular-nums;
}

// Popover das vendas por canal: uma linha por canal, com barra da participação
.share-rows {
    margin: 8px 0 0;
    padding: 0;
    list-style: none;
}

.share-row {
    display: grid;
    grid-template-columns: 8px 1fr auto;
    column-gap: 8px;
    row-gap: 3px;
    align-items: center;
    padding: 6px 0;

    & + & {
        border-top: 1px solid color-mix(in srgb, var(--text-on-tooltip) 10%, transparent);
    }
}

// Marcas escuras somem no fundo do popover: o anel claro mantém a bolinha visível
.share-row-swatch {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--channel-color);
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--text-on-tooltip) 30%, transparent);
}

.share-row-name {
    overflow: hidden;
    color: var(--text-on-tooltip);
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.share-row-percent {
    color: var(--text-on-tooltip);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.share-row-track {
    grid-column: 2 / -1;
    height: 3px;
    border-radius: 2px;
    background: color-mix(in srgb, var(--text-on-tooltip) 14%, transparent);
    overflow: hidden;
}

.share-row-fill {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: var(--channel-color);
}

.share-row-detail {
    grid-column: 2 / -1;
    color: var(--text-on-tooltip-muted);
    font-size: 11px;
    font-variant-numeric: tabular-nums;
}

.share-note {
    color: var(--text-on-tooltip-muted);
    font-size: 11px;
}

.order-ledger td.ledger-total {
    color: var(--brand);
    font-size: 1.3em;
    font-weight: 700;
    letter-spacing: -.3px;
    cursor: help;
}

.ledger-scope {
    color: var(--text-muted);
    font-size: .8em;
    line-height: 1.3;
    white-space: nowrap;
}

.ledger-scope-sub {
    display: block;
}

.ledger-fade-enter-active,
.ledger-fade-leave-active {
    transition: opacity .15s ease;
}

.ledger-fade-enter,
.ledger-fade-leave-to {
    opacity: 0;
}

.timeline-open-day-note {
    margin-top: 0;
    color: var(--text-muted);
    font-size: .9em;
    letter-spacing: 1px;
}

.pagination-day-hint {
    font-weight: 400;
    opacity: .7;
}

// O zebrado do Bootstrap (tbody tr:nth-of-type(odd)) reiniciaria a cada dia
// e ainda contaria a linha de cabeçalho, invertendo as faixas de grupo para
// grupo. Por isso a table-striped foi removida e o zebrado é feito aqui.
.order-row:nth-of-type(even) {
    background-color: color-mix(in srgb, var(--text-strong) 3%, transparent);
}

// A regra acima é mais específica que o .table-hover do Bootstrap, então o
// hover precisa ser redeclarado para continuar valendo nas linhas zebradas.
.order-row:hover,
.order-row:nth-of-type(even):hover {
    background-color: color-mix(in srgb, var(--text-strong) 7.5%, transparent);
}

// O rodapé fixo não funciona dentro de um ancestral com overflow, e o Bootstrap
// põe overflow-x no .table-responsive. No desktop a tabela cabe na largura.
@media (min-width: 992px) {
    .table-responsive {
        overflow: visible;
    }
}

// Abaixo deste ponto a tabela volta a rolar horizontalmente, e o overflow
// recortaria trilha e marcadores. O dia em aberto continua sinalizado pelo "N+",
// pelo rótulo do botão e pela nota do rodapé.
@media (max-width: 991px) {
    .order-timeline {
        padding-left: 0;

        .table-responsive {
            overflow-x: auto;
        }
    }

    .timeline-continues {
        display: none;
    }

    .timeline-day td:first-child::before,
    .order-row td:first-child::before,
    .order-ledger td:first-child::before,
    .timeline-day td:first-child::after {
        display: none;
    }

    // Com rolagem horizontal e o menu inferior do mobile, o rodapé vira só a última linha.
    .order-ledger td {
        position: static;
        box-shadow: none;
    }

    .timeline-day td:first-child {
        border-left: 3px solid var(--brand-fill);
    }

    .timeline-day.is-open-day td:first-child {
        border-left-style: dashed;
    }
}
</style>
